// eslint-disable-next-line no-restricted-imports
import type { CSSObject } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import { css } from '@emotion/react';

import BipocIcon from './icons/bipoc.svg?react';
import CaretIcon from './icons/caret.svg?react';
import CircleCheckFilledIcon from './icons/circleCheckFilled.svg?react';
import DollarSignIcon from './icons/dollar-sign.svg?react';
import InfoCircleIcon from './icons/info-circle.svg?react';
import InfoSquareIcon from './icons/info-square.svg?react';
import LgbtqIcon from './icons/lgbtq.svg?react';
import ProductsIcon from './icons/products.svg?react';
import ShoppingBagIcon from './icons/shopping_bag.svg?react';
import ShoppingCartSolidIcon from './icons/shopping-cart-solid.svg?react';
import ShoppingCartIcon from './icons/shopping-cart.svg?react';
import TimesIcon from './icons/times.svg?react';
import UsdSquareIcon from './icons/usd-square.svg?react';
import VeteranIcon from './icons/veteran.svg?react';
import WomanIcon from './icons/woman.svg?react';

export const icons = {
  bipoc: BipocIcon,
  caret: CaretIcon,
  circleCheckFilled: CircleCheckFilledIcon,
  dollarSign: DollarSignIcon,
  infoCircle: InfoCircleIcon,
  infoSquare: InfoSquareIcon,
  lgbtq: LgbtqIcon,
  products: ProductsIcon,
  shoppingBag: ShoppingBagIcon,
  shoppingCart: ShoppingCartIcon,
  shoppingCartSolid: ShoppingCartSolidIcon,
  times: TimesIcon,
  usdSquare: UsdSquareIcon,
  veteran: VeteranIcon,
  woman: WomanIcon,
};

type Size = number | { height?: number; width?: number };

interface NudgeProps {
  down?: number;
  left?: number;
  right?: number;
  up?: number;
}

interface Props {
  ariaHidden?: boolean;
  ariaLabel?: string;
  color?: string;
  dim?: boolean;
  display?: string;
  hidden?: boolean;
  icon: keyof typeof icons;
  nudge?: NudgeProps;
  role?: string;
  rotate?: 'left' | 'right' | '180';
  scale?: number;
  size?: Size;
  styles?: CSSObject;
}

const transform = (
  rotate: Props['rotate'],
  scale: Props['scale'],
  nudge: Props['nudge']
) => {
  const transformValues = [];

  switch (rotate) {
    case 'left':
      transformValues.push('rotate(-90deg)');
      break;
    case 'right':
      transformValues.push('rotate(90deg)');
      break;
    case '180':
      transformValues.push('rotate(-180deg)');
      break;
  }

  if (scale) transformValues.push(`scale(${scale})`);
  if (nudge) {
    const nudgeX = nudge.right
      ? `${nudge.right}px`
      : nudge.left
        ? `-${nudge.left}px`
        : 0;
    const nudgeY = nudge.down
      ? `${nudge.down}px`
      : nudge.up
        ? `-${nudge.up}px`
        : 0;
    transformValues.push(`translate(${nudgeX}, ${nudgeY})`);
  }

  if (transformValues.length === 0) return undefined;
  return transformValues.join(' ');
};

export function SvgIcon({
  color,
  dim = !color,
  icon,
  nudge,
  rotate = undefined,
  size = 18,
  scale,
  hidden,
  display,
  ariaHidden,
  ariaLabel,
  role,
  styles,
}: Props) {
  const Icon = icons[icon];
  if (!Icon) {
    return null;
  }

  return (
    <Icon
      // eslint-disable-next-line react/forbid-component-props -- legacy emotion
      css={css(
        typeof size === 'number' ? { height: size, width: size } : size,
        {
          fill: color || 'currentColor',
          transform: transform(rotate, scale, nudge),
          visibility: hidden ? 'hidden' : undefined,
          opacity: dim ? 0.9 : 1.0,
          display,
          ...styles,
        }
      )}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      role={role}
    />
  );
}
